import { addDays } from '../../constants/schedule';
import { previsaoDeParto } from '../../constants/workflowGranular';

/**
 * QUEM ESTÁ PRENHA AGORA, e quando deve parir.
 *
 * Vale o ÚLTIMO diagnóstico da receptora: a DG60 quando tem resultado, senão a
 * DG30. Abortou na DG60 sai, mesmo com PRENHE na DG30. PRENHE só na DG30 entra,
 * como "aguarda DG60": no dia a dia a maioria das prenhas está nesse ponto, e
 * deixá-las de fora faria o cliente parecer sem nenhuma.
 *
 * O parto é sempre TE + gestação, da data de agora. Não usa o `expected_birth`
 * gravado: ele não acompanha a TE quando a data dela é corrigida, e por isso
 * estava 23 dias errado nos serviços 2026-0061 e 2026-0062.
 *
 * O sistema não registra o parto. Sem um limite, a lista cresceria para sempre
 * com vaca que já pariu; passados estes dias da data prevista, ela sai.
 */
export const DIAS_NA_LISTA_DEPOIS_DO_PARTO = 30;

const hojeLocal = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};

/**
 * Nome da receptora primeiro: a DG30 e a DG60 da mesma vaca podem estar ligadas
 * de formas diferentes, e pelo vínculo ela contaria duas vezes (abortou numa
 * linha e seguiria prenha na outra).
 */
const chaveDaReceptora = (l) => `${l.service_id}|${
  String(l.recipient_identifier || '').trim().toUpperCase() || l.te_transfer_id || l.check_id}`;

const maisRecente = (a, b) => String(a.session_date || '') >= String(b.session_date || '');

/** Linhas de `vw_diagnosticos_de_prenha` → as prenhas de hoje, por data de parto. */
export const prenhasDeAgora = (linhas = [], hoje = hojeLocal()) => {
  const vale = new Map();
  linhas.forEach(l => {
    const chave = chaveDaReceptora(l);
    const atual = vale.get(chave);
    const ganha = !atual
      || (l.etapa === 'DG60' && atual.etapa !== 'DG60')
      || (l.etapa === atual.etapa && maisRecente(l, atual));
    if (ganha) vale.set(chave, l);
  });

  return [...vale.values()]
    .filter(l => l.result === 'PRENHE')
    .map(l => {
      const parto = previsaoDeParto(l.te_date);
      return {
        ...l,
        chave: l.check_id,
        confirmada: l.etapa === 'DG60',
        parto_previsto: parto,
        passou_da_data: !!parto && parto < hoje,
      };
    })
    .filter(p => !p.parto_previsto || addDays(p.parto_previsto, DIAS_NA_LISTA_DEPOIS_DO_PARTO) >= hoje)
    // Sem data vai para o fim: é a que menos se sabe quando vai parir.
    .sort((a, b) => String(a.parto_previsto || '9999').localeCompare(String(b.parto_previsto || '9999')));
};

/** A primeira data ainda por vir. A que já passou tem marca própria na linha. */
const proximoParto = (prenhas) =>
  prenhas.find(p => p.parto_previsto && !p.passou_da_data)?.parto_previsto || null;

/** As prenhas agrupadas por cliente, quem tem mais primeiro. */
export const prenhasPorCliente = (prenhas = []) => {
  const grupos = new Map();
  prenhas.forEach(p => {
    const chave = p.client_id || p.client_name || '—';
    const g = grupos.get(chave)
      || { chave, client_id: p.client_id || null, client_name: p.client_name || 'Sem cliente', prenhas: [] };
    g.prenhas.push(p);
    grupos.set(chave, g);
  });
  return [...grupos.values()]
    .map(g => ({
      ...g,
      confirmadas: g.prenhas.filter(p => p.confirmada).length,
      proximo_parto: proximoParto(g.prenhas),
    }))
    .sort((a, b) => b.prenhas.length - a.prenhas.length
      || a.client_name.localeCompare(b.client_name, 'pt-BR'));
};

/** Os totais, na forma em que a tela e o assistente os dizem. */
export const resumoDasPrenhas = (prenhas = []) => {
  const confirmadas = prenhas.filter(p => p.confirmada).length;
  return {
    total: prenhas.length,
    confirmadas,
    aguardam: prenhas.length - confirmadas,
    clientes: new Set(prenhas.map(p => p.client_id || p.client_name)).size,
    proximo_parto: proximoParto(prenhas),
  };
};

/** O que a coluna de situação diz. Estado por texto e marca, não por cor. */
export const situacaoDaPrenha = (p, MARK) => {
  if (p.passou_da_data) return `${MARK.WARN} Passou da data`;
  return p.confirmada ? `${MARK.OK} Confirmada` : `${MARK.PENDING} Aguarda DG60`;
};
